import * as Yup from 'yup';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useCreateDataAgentSourceMutation, useUpdateDataAgentSourceMutation } from '../../../api/dataAgentApiSlice';
import { showToaster } from '../../../utils/globalImports';

interface ColumnMetadata {
  columnName: string;
  description: string;
  synonymsNotes: string;
  sourceInstructions: string;
}

interface DataAgentSourceValues {
  dataAgentId: string;
  customerId: string;
  clientId: string;
  sourceType: string;
  lakehouseId?: string;
  fileId?: string;
  dataConnectionId?: string;
  sourceInstructions?: string;
  columnMetadata: ColumnMetadata[];
  isActive?: boolean;
}
export const useDataAgentSourceManagement = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [createDataAgentSource] = useCreateDataAgentSourceMutation();
  const [updateDataAgentSource] = useUpdateDataAgentSourceMutation();

  const validationSchema = Yup.object().shape({
    sourceType: Yup.string().required(t('dataAgents.validation.sourceTypeRequired')),
    lakehouseId: Yup.string().when('sourceType', {
      is: 'Lakehouse',
      then: (schema) => schema.required(t('dataAgents.validation.lakehouseRequired')),
      otherwise: (schema) => schema.notRequired(),
    }),
    fileId: Yup.string().when('sourceType', {
      is: 'Lakehouse',
      then: (schema) => schema.required(t('dataAgents.validation.fileRequired')),
      otherwise: (schema) => schema.notRequired(),
    }),
    dataConnectionId: Yup.string().when('sourceType', {
      is: 'DataConnection',
      then: (schema) => schema.required(t('dataAgents.validation.dataConnectionRequired')),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

  const handleSubmit = async (values: DataAgentSourceValues) => {
    // Set source_id based on source type
    let sourceId: string | undefined;
    if (values.sourceType === 'Lakehouse') {
      sourceId = values.lakehouseId;
    } else if (values.sourceType === 'DataConnection') {
      // For DataConnection, use the data_connection_id as the source_id
      sourceId = values.dataConnectionId;
    }

    const payload = {
      data_agent_id: values.dataAgentId,
      customer_id: values.customerId,
      client_id: values.clientId,
      source_type: values.sourceType,
      lakehouse_id: values.sourceType === 'Lakehouse' ? values.lakehouseId : null,
      source_id: sourceId, // This now contains the dataConnectionId for DataConnection type
      file_id: values.sourceType === 'Lakehouse' ? values.fileId : null,
      data_connection_id: values.sourceType === 'DataConnection' ? values.dataConnectionId : null,
      source_instructions: values.sourceInstructions,
      column_metadata: values.columnMetadata,
      is_active: values.isActive !== undefined ? values.isActive : true, // Add isActive with default true
    };

    console.log('Create new records Payload:', payload);

    try {
      const response = await createDataAgentSource(payload).unwrap();

      // Show success toaster
      dispatch(
        showToaster({
          message: t('dataAgents.apiMessages.dataAgentSourceCreatedSuccess'),
          type: 'success',
        }),
      );

      return { success: true, data: response };
    } catch (error: any) {
      console.error('Failed to create data agent source:', error);

      // Show error toaster
      dispatch(
        showToaster({
          message: error?.data?.message || t('dataAgents.apiMessages.dataAgentSourceCreatedError'),
          type: 'error',
        }),
      );

      return { success: false, error };
    }
  };

  const handleUpdate = async (dataAgentSourceId: string, values: DataAgentSourceValues) => {
    const payload = {
      data_agent_source_id: dataAgentSourceId,
      data_agent_id: values.dataAgentId,
      customer_id: values.customerId,
      client_id: values.clientId,
      source_type: values.sourceType,
      lakehouse_id: values.sourceType === 'Lakehouse' ? values.lakehouseId : null,
      data_table_id: values.sourceType === 'Lakehouse' ? values.fileId : null,
      data_connection_id: values.sourceType === 'DataConnection' ? values.dataConnectionId : null,
      source_instructions: JSON.stringify({
        sourceInstructions: values.sourceInstructions,
        columnMetadata: values.columnMetadata,
      }),
    };

    try {
      const response = await updateDataAgentSource(payload).unwrap();

      // Show success toaster
      dispatch(
        showToaster({
          message: t('dataAgents.apiMessages.dataAgentSourceUpdatedSuccess'),
          type: 'success',
        }),
      );

      return { success: true, data: response };
    } catch (error: any) {
      console.error('Failed to update data agent source:', error);

      // Show error toaster
      dispatch(
        showToaster({
          message: error?.data?.message || t('dataAgents.apiMessages.dataAgentSourceUpdatedError'),
          type: 'error',
        }),
      );

      return { success: false, error };
    }
  };

  return { validationSchema, handleSubmit, handleUpdate };
};
